import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import TextButton from 'LuxKit/Button/TextButton'
import ResponsiveDropdownList from 'tripPlanner/components/Common/Dropdowns/ResponsiveDropdownList'
import Group from 'components/utils/Group'
import FlightSingleFilterStops from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterStops'
import FlightSingleFilterBags from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterBags'
import FlightSingleFilterAirlines from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterAirlines'
import FlightSingleFilterPrice from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterPrice'
import FlightSingleFilterTime from 'components/Flights/FlightFiltering/FlightSingleFilter/FlightSingleFilterTime'
import useFlightFilterLabels from 'hooks/Flights/useFlightFilterLabels'
import { FlightFilterType } from 'constants/flight'
import type { FlightsFiltersValues } from 'lib/flights/flightFilterTypes'
import { useAssistantSurfaceDropdownBoundary } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'
import type { UseTripFlightFiltersResult } from './useTripFlightFilters'

interface Props {
  filterControls: UseTripFlightFiltersResult
  originAirportCode: string
  destinationAirportCode: string
}

const ChipsRow = styled(Group)`
  flex-wrap: wrap;
`

// When a filter is active, keep the outline variant but give it a thicker,
// higher-contrast border so the selected state reads clearly.
const StyledChip = styled(AssistChip)`
  &.is-active {
    &&& {
      --chip-stroke-width: 2px;
      --chip-stroke-color: ${({ theme }) => theme.palette.neutral.surface.normalContrast};
    }
  }
`

// Portaled + fixed positioning escapes chat-column stacking contexts (map overlap)
// and overflow clipping, while Floating UI shift keeps right-edge chips on-screen.
const DropdownContent = styled.div`
  min-width: ${rem(240)};
  max-width: min(${rem(360)}, calc(100dvw - ${rem(32)}));
  padding: 0 ${rem(16)};
`

interface FilterChipDropdownProps {
  label: string
  isActive: boolean
  count?: number
  children: React.ReactNode
}

/** A single filter rendered as a chip that toggles its own anchored dropdown. */
function FilterChipDropdown({ label, isActive, count, children }: FilterChipDropdownProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownBoundary = useAssistantSurfaceDropdownBoundary()
  const chipLabel = useMemo(() => count ? `${label} · ${count}` : label, [count, label])
  const endIcon = useMemo(() => <LineAngleDownIcon />, [])
  const handleToggleOpen = useCallback(() => setOpen(prev => !prev), [])
  const handleClose = useCallback(() => setOpen(false), [])

  return (
    <>
      <StyledChip
        className={clsx({ 'is-active': isActive })}
        variant="outline"
        size="medium"
        endIcon={endIcon}
        onClick={handleToggleOpen}
        ref={triggerRef}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {chipLabel}
      </StyledChip>

      {open && (
        <ResponsiveDropdownList
          size="S"
          open
          onClose={handleClose}
          triggerRef={triggerRef}
          anchorRef={triggerRef}
          boundaryRef={dropdownBoundary.boundaryRef}
          portaled={dropdownBoundary.portaled ?? true}
          autoHeight={dropdownBoundary.autoHeight}
          placement="bottom-start"
          crossAxisShift
          mobileTitle={label}
          data-testid="chat-flight-filter-dropdown-list"
        >
          <DropdownContent role="dialog" aria-label={label}>
            {children}
          </DropdownContent>
        </ResponsiveDropdownList>
      )}
    </>
  )
}

/**
 * The "Core 6" flight filters rendered as a row of chips, each opening its own
 * dropdown. Driven entirely by {@link useTripFlightFilters}; reuses the same
 * `FlightSingleFilter*` controls as the main flight search.
 */
function TripFlightFiltersPanel({ filterControls, originAirportCode, destinationAirportCode }: Props) {
  const intl = useIntl()
  const { flightFiltersModalLabelMap } = useFlightFilterLabels()
  const {
    filters,
    filterItems,
    activeFilters,
    onFilterChange,
    onPriceTypeChange,
    priceType,
    activeFilterCount,
    resetAllFilters,
  } = filterControls

  // The slider/checkbox controls emit native change events whose `name` is the
  // raw input name; map those to the corresponding filter field, mirroring the
  // standalone search panel.
  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    let name = e.target.name.replace('::number', '')
    const value = e.target.value

    if (name === 'price.start') name = 'minPrices'
    if (name === 'price.end') name = 'maxPrices'
    if (name === 'minDepartureTime.start') name = 'minDepartureTime'
    if (name === 'minDepartureTime.end') name = 'maxDepartureTime'
    if (name === 'minArrivalTime.start') name = 'minArrivalTime'
    if (name === 'minArrivalTime.end') name = 'maxArrivalTime'

    onFilterChange(name as keyof FlightsFiltersValues, value)
  }, [onFilterChange])

  const isTimeActive = !!(filters.minDepartureTime || filters.maxDepartureTime || filters.minArrivalTime || filters.maxArrivalTime)
  const isPriceActive = !!(filters.minPrices || filters.maxPrices)

  return (
    <ChipsRow direction="horizontal" gap={8} verticalAlign="center">
      {filterItems[FlightFilterType.STOPS].length > 2 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.STOPS]}
          isActive={filters.stops.length > 0}
          count={filters.stops.length}
        >
          <FlightSingleFilterStops
            items={filterItems[FlightFilterType.STOPS]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.STOPS]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.BAGS].length > 0 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.BAGS]}
          isActive={filters.bags.length > 0}
          count={filters.bags.length}
        >
          <FlightSingleFilterBags
            items={filterItems[FlightFilterType.BAGS]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.BAGS]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.AIRLINES].length > 2 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.AIRLINES]}
          isActive={filters.airlines.length > 0}
          count={filters.airlines.length}
        >
          <FlightSingleFilterAirlines
            items={filterItems[FlightFilterType.AIRLINES]}
            onFilterChange={handleFilterChange}
            selectedItems={activeFilters[FlightFilterType.AIRLINES]}
          />
        </FilterChipDropdown>
      )}

      {filterItems[FlightFilterType.PRICES].length > 1 && (
        <FilterChipDropdown
          label={flightFiltersModalLabelMap[FlightFilterType.PRICES]}
          isActive={isPriceActive}
        >
          <FlightSingleFilterPrice
            items={filterItems[FlightFilterType.PRICES]}
            onFilterChange={handleFilterChange}
            selectedMinItem={[...activeFilters[FlightFilterType.MIN_PRICES]][0]}
            selectedMaxItem={[...activeFilters[FlightFilterType.MAX_PRICES]][0]}
            priceType={priceType}
            onPriceTypeChange={onPriceTypeChange}
          />
        </FilterChipDropdown>
      )}

      <FilterChipDropdown
        label={flightFiltersModalLabelMap[FlightFilterType.TIMES]}
        isActive={isTimeActive}
      >
        <FlightSingleFilterTime
          onFilterChange={handleFilterChange}
          selectedDepartureMinItem={[...activeFilters[FlightFilterType.MIN_DEPARTURE_TIME]][0]}
          selectedDepartureMaxItem={[...activeFilters[FlightFilterType.MAX_DEPARTURE_TIME]][0]}
          selectedArrivalMinItem={[...activeFilters[FlightFilterType.MIN_ARRIVAL_TIME]][0]}
          selectedArrivalMaxItem={[...activeFilters[FlightFilterType.MAX_ARRIVAL_TIME]][0]}
          departureAirportCode={originAirportCode}
          arrivalAirportCode={destinationAirportCode}
          departureTimeItems={filterItems[FlightFilterType.DEPARTURE_TIMES]}
          arrivalTimeItems={filterItems[FlightFilterType.ARRIVAL_TIMES]}
        />
      </FilterChipDropdown>

      {activeFilterCount > 0 && (
        <TextButton kind="tertiary" size="medium" onClick={resetAllFilters}>
          {intl.formatMessage({
            id: 'faLgDM2T7ihJeAe-9Gzs',
            defaultMessage: 'Clear all',
            description: 'Action label to clear all applied flight filters in the trip planner drawer',
          })}
        </TextButton>
      )}
    </ChipsRow>
  )
}

export default TripFlightFiltersPanel
